import { useCallback, useEffect, useMemo, useState } from 'react';
import { useStore } from '../../store';
import type { ChatGroup, ChatSession, WSOutgoingMessage } from '../../types';
import { GroupAvatar } from '../shared/GroupAvatar';
import { BotAvatar } from '../shared/BotAvatar';
import { CreateGroupModal } from './CreateGroupModal';
import { useT } from '../../utils/i18n';
import { relativeTime } from '../../utils/format-time';
import { getMembers } from '../../utils/members-cache';
import styles from './AgentList.module.css';

function getGroupColor(name: string): string | undefined {
  try { return localStorage.getItem(`novax:groupColor:${name}`) || undefined; } catch { return undefined; }
}

function sessionPreview(session: ChatSession, t: (k: string) => string): { text: string; status?: string } {
  if (session.messages.length === 0) return { text: t('chat.startConversation') };
  const last = session.messages[session.messages.length - 1];
  if (last.attachments && last.attachments.length > 0) {
    const f = last.attachments[0];
    const label = last.attachments.length > 1 ? `📎 ${f.name} +${last.attachments.length - 1}` : `📎 ${f.name}`;
    return { text: label };
  }
  const lastText = (last.text || last.state?.responseText || '')
    .replace(/::(danger|success|highlight)::(.*?)::/g, '$2')
    .replace(/\n+/g, ' ')
    .slice(0, 50);
  if (last.type === 'user') return { text: lastText };
  if (last.state) {
    if (last.state.status === 'thinking') return { text: t('msg.thinking'), status: 'thinking' };
    if (last.state.status === 'running') return { text: t('msg.running'), status: 'running' };
    if (last.state.status === 'error') return { text: last.state.errorMessage?.slice(0, 40) || t('msg.error'), status: 'error' };
  }
  return { text: lastText };
}


interface AgentListProps {
  send: (msg: WSOutgoingMessage) => void;
}

export function AgentList({ send }: AgentListProps) {
  const t = useT();
  const bots = useStore((s) => s.bots);
  const connected = useStore((s) => s.connected);
  const sessions = useStore((s) => s.sessions);
  const activeSessionId = useStore((s) => s.activeSessionId);
  const activeBotName = useStore((s) => s.activeBotName);
  const setActiveSession = useStore((s) => s.setActiveSession);
  const setBot = useStore((s) => s.setBot);
  const setView = useStore((s) => s.setView);
  const getOrCreateBotSession = useStore((s) => s.getOrCreateBotSession);
  const groups = useStore((s) => s.groups);

  const unreadDMs = useStore((s) => s.unreadDMs);
  const [searchQuery, setSearchQuery] = useState('');
  const token = useStore((s) => s.token);
  const [showCreateGroup, setShowCreateGroup] = useState(false);

  // Context menu for group management
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; group: ChatGroup } | null>(null);

  // Fetch workspace members for DM avatars/names
  const [memberMap, setMemberMap] = useState<Map<string, { name?: string; avatarUrl?: string }>>(new Map());
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    const { data, promise } = getMembers(token);
    if (data) { setMemberMap(data); } else { promise.then((m) => { if (!cancelled) setMemberMap(m); }); }
    return () => { cancelled = true; };
  }, [token]);

  // Close context menu on click outside
  useEffect(() => {
    if (!contextMenu) return;
    const handler = () => setContextMenu(null);
    window.addEventListener('click', handler);
    return () => window.removeEventListener('click', handler);
  }, [contextMenu]);

  // Group sessions (all groups, sorted: default first, then by recent activity)
  const groupSessions = useMemo(() => {
    const result: { group: ChatGroup; session: ChatSession | null }[] = [];
    for (const group of groups) {
      let session: ChatSession | null = null;
      for (const sess of sessions.values()) {
        if (sess.groupId === group.id) { session = sess; break; }
      }
      result.push({ group, session });
    }
    // Default group first, then sort by recent activity
    result.sort((a, b) => {
      if (a.group.isDefault && !b.group.isDefault) return -1;
      if (!a.group.isDefault && b.group.isDefault) return 1;
      const aTime = a.session?.updatedAt ?? a.group.createdAt;
      const bTime = b.session?.updatedAt ?? b.group.createdAt;
      return bTime - aTime;
    });
    return result;
  }, [groups, sessions]);

  // One session per bot
  const botSessions = useMemo(() => {
    const map = new Map<string, ChatSession>();
    for (const session of sessions.values()) {
      if (session.groupId) continue;
      const existing = map.get(session.botName);
      if (!existing || session.updatedAt > existing.updatedAt) {
        map.set(session.botName, session);
      }
    }
    return map;
  }, [sessions]);

  const handleBotClick = useCallback((botName: string) => {
    getOrCreateBotSession(botName);
    setView('chat');
  }, [getOrCreateBotSession, setView]);

  const handleGroupClick = useCallback((group: ChatGroup) => {
    // Find existing session for this group
    for (const sess of sessions.values()) {
      if (sess.groupId === group.id) {
        setActiveSession(sess.id);
        return;
      }
    }
    // Create session if not exists
    useStore.getState().createGroupSession(group);
  }, [sessions, setActiveSession]);

  const handleGroupContextMenu = useCallback((e: React.MouseEvent, group: ChatGroup) => {
    if (group.isDefault) return; // No context menu for default group
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({ x: e.clientX, y: e.clientY, group });
  }, []);

  const handleDeleteGroup = useCallback(() => {
    if (!contextMenu) return;
    if (confirm(t('chat.deleteGroupConfirm'))) {
      send({ type: 'delete_group', groupId: contextMenu.group.id });
    }
    setContextMenu(null);
  }, [contextMenu, send, t]);

  // Unified conversation list: bots + DM sessions + groups, all sorted by recent activity
  type ConvoItem =
    | { type: 'bot'; bot: typeof bots[0]; session?: ChatSession }
    | { type: 'dm'; session: ChatSession }
    | { type: 'group'; group: ChatGroup; session: ChatSession | null };

  const allConversations = useMemo(() => {
    const items: ConvoItem[] = [];
    // Add bots
    for (const bot of bots) {
      items.push({ type: 'bot', bot, session: botSessions.get(bot.name) });
    }
    // Add DM sessions
    const seenDm = new Set<string>();
    for (const session of sessions.values()) {
      if (!session.botName.startsWith('dm:') || session.groupId) continue;
      if (seenDm.has(session.botName)) continue;
      seenDm.add(session.botName);
      items.push({ type: 'dm', session });
    }
    // Add groups
    for (const { group, session } of groupSessions) {
      items.push({ type: 'group', group, session });
    }
    items.sort((a, b) => {
      const aTime = a.type === 'dm' ? a.session.updatedAt
        : a.type === 'group' ? (a.session?.updatedAt ?? a.group.createdAt)
        : (a.session?.updatedAt ?? 0);
      const bTime = b.type === 'dm' ? b.session.updatedAt
        : b.type === 'group' ? (b.session?.updatedAt ?? b.group.createdAt)
        : (b.session?.updatedAt ?? 0);
      return bTime - aTime;
    });
    return items;
  }, [bots, botSessions, sessions, groupSessions]);

  // Search filter
  const filteredConversations = useMemo(() => {
    if (!searchQuery.trim()) return allConversations;
    const q = searchQuery.toLowerCase();
    return allConversations.filter((item) => {
      if (item.type === 'bot') {
        if (item.bot.name.toLowerCase().includes(q)) return true;
        if (item.bot.description?.toLowerCase().includes(q)) return true;
        if (item.session) {
          return item.session.messages.some((msg) => msg.text?.toLowerCase().includes(q));
        }
        return false;
      }
      if (item.type === 'group') {
        return item.group.name.toLowerCase().includes(q);
      }
      // DM
      const name = item.session.title !== 'New Chat' ? item.session.title : item.session.botName.slice(3);
      if (name.toLowerCase().includes(q)) return true;
      return item.session.messages.some((msg) => msg.text?.toLowerCase().includes(q));
    });
  }, [allConversations, searchQuery]);

  return (
    <div className={styles.container}>
      {/* Brand strip header */}
      <div className={styles.header}>
        <h2 className={styles.title}>
          <span className={styles.titleNova}>Nova</span>
          <span className={styles.titleX}>X</span>
          <span className={styles.titleSub}> &middot; {t('chat.workspace')}</span>
        </h2>
      </div>

      {/* Search bar + create group button */}
      <div className={styles.searchRow}>
        <div className={styles.searchBar}>
          <svg className={styles.searchIcon} width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            className={styles.searchInput}
            type="text"
            placeholder={t('chat.search')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <button
          className={styles.newGroupBtn}
          onClick={() => setShowCreateGroup(true)}
          title={t('chat.newGroup')}
        >
          <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <line x1="19" y1="8" x2="19" y2="14" />
            <line x1="22" y1="11" x2="16" y2="11" />
          </svg>
        </button>
      </div>

      {/* Conversation list */}
      <div className={styles.list}>
        <div className={styles.sectionLabel}>
          {t('chat.conversations')} &middot; {filteredConversations.length}
        </div>

        {filteredConversations.length === 0 && (
          <div className={styles.empty}>
            {connected ? t('chat.noAgents') : t('chat.connecting')}
          </div>
        )}
        {filteredConversations.map((item) => {
          if (item.type === 'dm') {
            const session = item.session;
            const dmUserId = session.botName.slice(3);
            const member = memberMap.get(dmUserId);
            const displayName = member?.name || (session.title && session.title !== 'New Chat' ? session.title : 'User');
            const initial = displayName.charAt(0).toUpperCase();
            const isActive = activeSessionId === session.id;
            const hasUnread = unreadDMs.has(session.id);
            const preview = sessionPreview(session, t);
            return (
              <button
                key={session.id}
                className={`${styles.agentRow} ${isActive ? styles.agentRowActive : ''}`}
                onClick={() => setActiveSession(session.id)}
              >
                <div className={styles.avatarWrap}>
                  {member?.avatarUrl ? (
                    <img src={member.avatarUrl} alt="" className={styles.dmAvatarImg} />
                  ) : (
                    <div className={styles.dmAvatarInitial}>{initial}</div>
                  )}
                  {hasUnread && <span className={styles.unreadBadge} />}
                </div>
                <div className={styles.agentInfo}>
                  <div className={styles.nameRow}>
                    <span className={`${styles.agentName} ${hasUnread ? styles.agentNameUnread : ''}`}>{displayName}</span>
                    <span className={styles.time}>{relativeTime(session.updatedAt)}</span>
                  </div>
                  <div className={`${styles.preview} ${hasUnread ? styles.previewUnread : ''}`}>{preview.text}</div>
                </div>
              </button>
            );
          }

          if (item.type === 'group') {
            const { group, session: gSession } = item;
            const isActive = gSession ? activeSessionId === gSession.id : false;
            const gPreview = gSession && gSession.messages.length > 0
              ? sessionPreview(gSession, t).text
              : group.isDefault ? t('chat.groupDesc') : `${group.members.length} agents`;
            return (
              <button
                key={group.id}
                className={`${styles.agentRow} ${isActive ? styles.agentRowActive : ''}`}
                onClick={() => handleGroupClick(group)}
                onContextMenu={(e) => handleGroupContextMenu(e, group)}
              >
                <div className={styles.avatarWrap}>
                  <GroupAvatar size={36} color={getGroupColor(group.name)} />
                  <span className={`${styles.statusDot} ${connected ? styles.statusOnline : ''}`} />
                </div>
                <div className={styles.agentInfo}>
                  <div className={styles.nameRow}>
                    <span className={styles.agentName}>{group.name}</span>
                    {gSession && gSession.messages.length > 0 && (
                      <span className={styles.time}>{relativeTime(gSession.updatedAt)}</span>
                    )}
                  </div>
                  <div className={styles.preview}>{gPreview}</div>
                </div>
              </button>
            );
          }

          const bot = item.bot;
          const session = item.session;
          const preview = session && session.messages.length > 0
            ? sessionPreview(session, t)
            : { text: bot.description || '' };
          const isActive = session ? activeSessionId === session.id : (!activeSessionId && activeBotName === bot.name);
          return (
            <button
              key={bot.name}
              className={`${styles.agentRow} ${isActive ? styles.agentRowActive : ''}`}
              onClick={() => handleBotClick(bot.name)}
            >
              <div className={styles.avatarWrap}>
                <BotAvatar name={bot.name} size="md" />
                <span className={`${styles.statusDot} ${connected ? styles.statusOnline : ''}`} />
              </div>
              <div className={styles.agentInfo}>
                <div className={styles.nameRow}>
                  <span className={styles.agentName}>{bot.name}</span>
                  {session && (
                    <span className={styles.time}>{relativeTime(session.updatedAt)}</span>
                  )}
                </div>
                <div className={`${styles.preview} ${preview.status === 'error' ? styles.previewError : ''}`}>
                  {preview.text}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Context menu */}
      {contextMenu && (
        <div
          className={styles.contextMenu}
          style={{ top: contextMenu.y, left: contextMenu.x }}
        >
          <button className={styles.contextMenuItem} onClick={handleDeleteGroup}>
            <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" />
              <path d="M10 11v6M14 11v6" />
            </svg>
            {t('chat.deleteGroup')}
          </button>
        </div>
      )}

      {/* Create group modal */}
      {showCreateGroup && (
        <CreateGroupModal
          bots={bots}
          send={send}
          onClose={() => setShowCreateGroup(false)}
        />
      )}
    </div>
  );
}
